import { CODING_AGENT_LABELS } from '@littlebird/action-cards';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';

import { YStack } from '@/components/ui/layout/layout';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/Switch';

import { AgentActions } from './agent-actions';
import { AgentRow } from './agent-row';
import { agentStatusText, showsConnectSwitch } from './agent-status';
import { codingAgentsStore as store } from './coding-agents.store';
import { SetupProgressView } from './setup-progress-view';

// The agents shown until the first read of their setup ends
const PLACEHOLDER_IDS = Object.keys(CODING_AGENT_LABELS) as Array<
  keyof typeof CODING_AGENT_LABELS
>;

export const CodingAgentsPanel = observer(() => {
  useEffect(() => {
    void store.load();
  }, []);

  return (
    <YStack className="gap-3" aria-busy={!store.loaded}>
      <p className="text-sm text-foreground-muted">
        Let Littlebird connect with the coding agents on this Mac.
      </p>
      {store.loadError ? (
        <p role="alert" className="text-xs text-danger-11">
          {store.loadError}
        </p>
      ) : null}
      {store.loaded
        ? store.agents.map((agent) => {
            const progress = store.activities.get(agent.id);
            return (
              <AgentRow
                key={agent.id}
                id={agent.id}
                label={agent.label}
                actions={
                  <>
                    <AgentActions agent={agent} />
                    {showsConnectSwitch(agent, progress) ? (
                      <Switch
                        aria-label={`Connect ${agent.label}`}
                        checked={agent.connected}
                        disabled={store.busy.has(agent.id)}
                        onCheckedChange={(connected) =>
                          void store.setConnected(agent.id, connected)
                        }
                      />
                    ) : null}
                  </>
                }
              >
                {progress ? (
                  <SetupProgressView progress={progress} />
                ) : (
                  <p className="text-xs text-foreground-muted">
                    {agentStatusText(agent)}
                  </p>
                )}
                {agent.error ? (
                  <p role="alert" className="text-xs text-danger-11">
                    {agent.error}
                  </p>
                ) : null}
              </AgentRow>
            );
          })
        : PLACEHOLDER_IDS.map((id) => (
            <AgentRow key={id} id={id} label={CODING_AGENT_LABELS[id]}>
              {/* Sized to a line of status text */}
              <Skeleton className="my-0.5 h-3 w-32" />
            </AgentRow>
          ))}
    </YStack>
  );
});
